"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Settings,
  LogOut,
  Crown,
  Bell,
  Shield,
  History,
  Search,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Clock,
  MessageSquare,
  Loader2,
} from "lucide-react"
import { useAuth } from "./auth/auth-context"
import { Input } from "@/components/ui/input"

type HistoryItem = {
  id: string
  message: string
  improvedMessage: string
  score: number
  mode: "casual" | "professional" | "flirty" | "sarcastic"
  date: string
  redFlags: string[]
}

type ProfileScreenProps = {
  setActiveScreen: (screen: string) => void
  initialTab?: "profile" | "history"
}

export function ProfileScreen({ setActiveScreen, initialTab = "profile" }: ProfileScreenProps) {
  const { user, logout, isAnonymous } = useAuth()
  const [activeTab, setActiveTab] = useState<string>(initialTab || "profile")
  const [searchTerm, setSearchTerm] = useState("")
  const [activeFilter, setActiveFilter] = useState<string | null>(null)
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)

  // Mock history data
  const mockHistory: HistoryItem[] = [
    {
      id: "msg1",
      message: "Hey, wanna hang out later?",
      improvedMessage: "Hey! Would you like to hang out later? I was thinking we could grab coffee.",
      score: 8,
      mode: "casual",
      date: "Today, 2:30 PM",
      redFlags: [],
    },
    {
      id: "msg2",
      message: "I really enjoyed our date last night, would love to see you again!",
      improvedMessage:
        "I had a wonderful time on our date last night! I'd love to see you again soon. Are you free this weekend?",
      score: 6,
      mode: "flirty",
      date: "Yesterday, 9:15 AM",
      redFlags: ["Could be more specific", "Lacks a question to encourage response"],
    },
    {
      id: "msg3",
      message: "I wanted to follow up on our meeting from yesterday!!!",
      improvedMessage:
        "I wanted to follow up on our meeting from yesterday. Do you have any questions about the proposal I presented?",
      score: 4,
      mode: "professional",
      date: "Jan 15, 2023",
      redFlags: ["Excessive punctuation", "Too vague", "Lacks professional tone"],
    },
  ]

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0 },
  }

  const handleLogout = () => {
    logout()
    setActiveScreen("login")
  }

  const getScoreColor = (score: number) => {
    if (score >= 8) return "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
    if (score >= 5) return "bg-amber-500/20 text-amber-400 border border-amber-500/30"
    return "bg-rose-500/20 text-rose-400 border border-rose-500/30"
  }

  const getScoreEmoji = (score: number) => {
    if (score >= 8) return "🔥"
    if (score >= 5) return "😐"
    return "🚩"
  }

  const getModeEmoji = (mode: string) => {
    switch (mode) {
      case "casual":
        return "👋"
      case "professional":
        return "👔"
      case "flirty":
        return "😏"
      case "sarcastic":
        return "😒"
      default:
        return "💬"
    }
  }

  useEffect(() => {
    if (activeTab === "history" && user && !isAnonymous) {
      fetchUserHistory()
    }
  }, [activeTab, user, isAnonymous])

  const fetchUserHistory = async () => {
    setIsHistoryLoading(true)

    try {
      const token = localStorage.getItem("token")

      if (!token) {
        throw new Error("Not authenticated")
      }

      const response = await fetch("/api/analysis", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Failed to fetch history")
      }

      const historyData = await response.json()
      setHistory(historyData)
    } catch (error) {
      console.error("Error fetching history:", error)
      // Use mock data as fallback
      setHistory(mockHistory)
    } finally {
      setIsHistoryLoading(false)
    }
  }

  const filteredHistory = (history.length > 0 ? history : mockHistory).filter((item) => {
    // Apply search filter
    const matchesSearch =
      searchTerm === "" ||
      item.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.improvedMessage.toLowerCase().includes(searchTerm.toLowerCase())

    // Apply mode filter
    const matchesFilter = activeFilter === null || item.mode === activeFilter

    return matchesSearch && matchesFilter
  })

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this analysis?")) return

    try {
      const token = localStorage.getItem("token")
      const response = await fetch(`/api/analysis/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
  
      if (!response.ok) {
        throw new Error("Failed to delete item")
      }
  
      // Remove from local state
      setHistory((prev) => prev.filter((item) => item.id !== id))
    } catch (error) {
      console.error("Error deleting analysis:", error)
      alert("Could not delete message. Please try again.")
    }
  }
  

  return (
    <div className="flex flex-col h-full p-5 pt-8">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-light">
            <span className="gradient-text font-medium">My Account</span>
          </h1>
        </div>

        <div className="flex items-center mb-4">
          <div className="w-14 h-14 rounded-full gradient-bg flex items-center justify-center text-xl">
            {user?.name.charAt(0) || "🤩"}
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-light">{user?.name || "User"}</h2>
            <div className="flex items-center">
              <Badge
                className={`${user?.plan === "premium" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "glass"} text-white text-xs rounded-full px-2 font-light`}
              >
                {user?.plan === "premium" ? "Premium Plan" : "Free Plan"}
              </Badge>
              {user?.plan === "free" && (
                <span className="text-zinc-400 text-sm ml-2 font-light">{user?.scansLeft || 0} scans left</span>
              )}
            </div>
          </div>
        </div>

        <Tabs value={activeTab as string} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 glass mb-4">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
        </Tabs>
      </motion.div>

      <div className="flex-1 overflow-auto pb-4">
        {activeTab === "profile" && (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 gap-3 auto-rows-auto"
          >
            {/* Premium card - only show for free users */}
            {user?.plan === "free" && (
              <motion.div
                variants={item}
                className="col-span-2 gradient-border-amber glass-card rounded-xl p-3 cursor-pointer"
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveScreen("upgrade")}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-light flex items-center">
                    <Crown className="h-4 w-4 mr-2 text-amber-400" />
                    Upgrade to Premium
                  </h3>
                  <Badge className="bg-amber-500/20 text-amber-400 font-light rounded-full px-3 border border-amber-500/30">
                    $6.99/mo
                  </Badge>
                </div>

                <ul className="space-y-1 mb-3">
                  {["Unlimited message scans", "All tone modes", "AI-generated rewrites"].map((feature, index) => (
                    <li key={index} className="text-sm flex items-start text-zinc-300 font-light">
                      <span className="mr-2 text-amber-400">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-light rounded-xl hover:opacity-90">
                  Upgrade Now
                </Button>
              </motion.div>
            )}

            {/* Stats cards */}
            <motion.div variants={item} className="col-span-1 glass-card rounded-xl p-3" whileHover={{ y: -5 }}>
              <div className="flex flex-col h-full justify-between">
                <div className="flex items-center text-zinc-400 mb-1">
                  <History className="h-4 w-4 mr-1" />
                  <span className="text-xs font-light">History</span>
                </div>
                <div>
                  <div className="text-2xl font-light">{history.length > 0 ? history.length : mockHistory.length}</div>
                  <div className="text-xs text-zinc-400 font-light">Messages</div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={item} className="col-span-1 glass-card rounded-xl p-3" whileHover={{ y: -5 }}>
              <div className="flex flex-col h-full justify-between">
                <div className="flex items-center text-zinc-400 mb-1">
                  <Bell className="h-4 w-4 mr-1" />
                  <span className="text-xs font-light">Alerts</span>
                </div>
                <div>
                  <div className="text-2xl font-light">3</div>
                  <div className="text-xs text-zinc-400 font-light">Notifications</div>
                </div>
              </div>
            </motion.div>

            {/* Settings cards */}
            <motion.div variants={item} className="col-span-2 glass-card rounded-xl overflow-hidden">
              {[
                {
                  icon: Bell,
                  label: "Notifications",
                  color: "bg-pink-500/20",
                  action: () => console.log("Notifications clicked"),
                },
                {
                  icon: Shield,
                  label: "Privacy Settings",
                  color: "bg-emerald-500/20",
                  action: () => console.log("Privacy clicked"),
                },
                {
                  icon: MessageSquare,
                  label: "Feedback",
                  color: "bg-blue-500/20",
                  action: () => console.log("Feedback clicked"),
                },
              ].map((item, index) => (
                <motion.button
                  key={index}
                  className="w-full p-3 flex items-center justify-between border-b border-white/5 last:border-b-0"
                  whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
                  onClick={item.action}
                >
                  <div className="flex items-center">
                    <div
                      className={`${item.color} h-8 w-8 rounded-full flex items-center justify-center mr-3 border border-white/10`}
                    >
                      <item.icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-light">{item.label}</span>
                  </div>
                  <div className="text-zinc-500">
                    <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1 9L5 5L1 1"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </motion.button>
              ))}
            </motion.div>

            <motion.button
              variants={item}
              className="col-span-2 glass-card rounded-xl p-3 flex items-center text-rose-400 mt-2"
              whileHover={{ y: -5, backgroundColor: "rgba(255, 255, 255, 0.05)" }}
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-3" />
              <span className="font-light">Log Out</span>
            </motion.button>
          </motion.div>
        )}

        {activeTab === "history" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-10 pl-10"
              />
            </div>

            <div className="flex space-x-2 mb-3 overflow-x-auto pb-2 scrollbar-hide">
              <Button
                variant={activeFilter === null ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${activeFilter === null ? "gradient-bg" : "glass"}`}
                onClick={() => setActiveFilter(null)}
              >
                All
              </Button>
              {["casual", "professional", "flirty", "sarcastic"].map((mode) => (
                <Button
                  key={mode}
                  variant={activeFilter === mode ? "default" : "outline"}
                  size="sm"
                  className={`rounded-full ${activeFilter === mode ? "gradient-bg" : "glass"}`}
                  onClick={() => setActiveFilter(mode)}
                >
                  <span className="mr-1">{getModeEmoji(mode)}</span>
                  <span className="capitalize">{mode}</span>
                </Button>
              ))}
            </div>

            {isHistoryLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-rose-400" />
              </div>
            )}

            {filteredHistory.length > 0 ? (
              <div className="space-y-4">
                {filteredHistory.map((historyItem) => (
                  <motion.div
                    key={historyItem.id}
                    className="glass-card rounded-xl overflow-hidden"
                    whileHover={{ y: -5 }}
                  >
                    <Tabs defaultValue="original" className="w-full">
                      <div className="flex items-center justify-between p-3 border-b border-white/10">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">{getModeEmoji(historyItem.mode)}</span>
                          <div>
                            <div className="text-sm font-medium capitalize">{historyItem.mode}</div>
                            <div className="text-xs text-zinc-400">{historyItem.date}</div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Badge className={`${getScoreColor(historyItem.score)} px-3 py-1 rounded-full`}>
                            <span>
                              {getScoreEmoji(historyItem.score)} {historyItem.score}/10
                            </span>
                          </Badge>

                          <TabsList className="glass">
                            <TabsTrigger value="original" className="text-xs">
                              Original
                            </TabsTrigger>
                            <TabsTrigger value="improved" className="text-xs">
                              Improved
                            </TabsTrigger>
                          </TabsList>
                        </div>
                      </div>

                      <TabsContent value="original" className="p-3 text-sm">
                        <div className="mb-3">{historyItem.message}</div>

                        {historyItem.redFlags.length > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center text-rose-400 text-xs mb-1">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              <span>Red Flags</span>
                            </div>
                            <ul className="text-xs text-zinc-400 pl-4">
                              {historyItem.redFlags.map((flag, idx) => (
                                <li key={idx} className="list-disc">
                                  {flag}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="improved" className="p-3 text-sm">
                        <div className="flex items-center text-emerald-400 text-xs mb-1">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          <span>Improved Version</span>
                        </div>
                        <div>{historyItem.improvedMessage}</div>
                      </TabsContent>

                      <div className="flex border-t border-white/10 divide-x divide-white/10">
                        <Button
                          variant="ghost"
                          className="flex-1 rounded-none h-10 text-xs text-zinc-400 hover:text-white hover:bg-white/5"
                          onClick={() => {
                            navigator.clipboard.writeText(historyItem.improvedMessage)
                            alert("Copied to clipboard!")
                          }}
                        >
                          Copy Improved
                        </Button>
                        <Button
                          variant="ghost"
                          className="flex-1 rounded-none h-10 text-xs text-zinc-400 hover:text-rose-400 hover:bg-white/5"
                          onClick={() => handleDelete(historyItem.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </Tabs>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                className="flex flex-col items-center justify-center h-full text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Clock className="h-12 w-12 text-zinc-600 mb-4" />
                <h3 className="text-xl font-medium mb-2">No messages found</h3>
                <p className="text-zinc-400 mb-6 max-w-xs">
                  {searchTerm || activeFilter
                    ? "Try adjusting your search or filters"
                    : "Your message history will appear here after you analyze messages"}
                </p>
                <Button
                  className="gradient-bg text-white hover:opacity-90 rounded-xl"
                  onClick={() => setActiveScreen("analyze")}
                >
                  Analyze a Message
                </Button>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  )
}

